// src/pages/onboarding/KeywordsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import api from '../../utils/apiClient';

// Keyword suggestion card
const KeywordItem = ({ keyword, onSelect, selected }) => {
  const { text, custom } = keyword;
  return (
    <div
      onClick={() => onSelect(keyword)}
      className={`
        border rounded-lg p-4 cursor-pointer transition-all
        ${selected ? 'border-[#1ba6ff] bg-[#1ba6ff]/10' : 'border-gray-200 hover:border-gray-300'}
      `}
    >
      <div className="flex justify-between items-start">
        <h3 className="text-md font-medium text-gray-900">{text}</h3>
      </div>
    </div>
  );
};

const KeywordsPage = ({ isSettings = false }) => {
  const navigate = useNavigate();
  const { currentProject, loadCurrentProject } = useProjects();

  // form state
  const [keywords, setKeywords]           = useState([]);
  const [selectedKeywords, setSelected]   = useState([]);
  const [customInput, setCustomInput]     = useState('');
  const [regionName, setRegionName]       = useState('');
  const [loading, setLoading]             = useState(false);
  const [errorMsg, setErrorMsg]           = useState('');
  const [successMsg, setSuccessMsg]       = useState('');
  const [isGenerating, setIsGenerating]   = useState(false);

  const projectKeywordsFingerprint = Array.isArray(currentProject?.project_keywords)
    ? currentProject.project_keywords.map(k => k.keyword).sort().join('\u0001')
    : '';

  // Load region/keywords when project id or server keyword list changes (e.g. after cache refresh)
  useEffect(() => {
    if (!currentProject?.id) return;

    if (currentProject.keyword_region) {
      setRegionName(currentProject.keyword_region);
    }

    if (Array.isArray(currentProject.project_keywords)) {
      const pre = currentProject.project_keywords.map(k => ({
        id: `pre-${k.keyword}`,
        text: k.keyword,
        custom: true
      }));
      setSelected(pre);
    }
  }, [currentProject?.id, projectKeywordsFingerprint]);

  // Function to generate suggestions
  const generateSuggestions = async () => {
    if (!currentProject?.website || !currentProject?.description) {
      setErrorMsg('Project details are required to generate suggestions');
      return;
    }

    setIsGenerating(true);
    setErrorMsg('');
    
    try {
      const response = await api.keywords.getSuggestions(currentProject.id);
      setKeywords(response.data.suggestions.map((suggestion, idx) => ({
        id: idx + 1,
        text: suggestion.keyword
      })));
    } catch (err) {
      console.error('Error loading keyword suggestions:', err);
      setErrorMsg('Failed to load keyword suggestions');
    } finally {
      setIsGenerating(false);
    }
  };

  // select/deselect
  const handleSelect = (kw) => {
    if (selectedKeywords.find(k=>k.text===kw.text)) {
      setSelected(selectedKeywords.filter(k=>k.text!==kw.text));
    } else {
      setSelected([...selectedKeywords, kw]);
    }
  };

  // add custom keyword
  const addCustom = () => {
    const txt = customInput.trim();
    if (!txt) return;
    const newKw = { id: `c-${txt}`, text: txt, custom:true };
    if (!selectedKeywords.find(k=>k.text===txt)) {
      setSelected([...selectedKeywords, newKw]);
    }
    setCustomInput('');
  };
  const handleCustomKey = e => {
    if (e.key==='Enter') {
      e.preventDefault();
      addCustom();
    }
  };

  // save region + keywords
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!regionName.trim()) {
      setErrorMsg('Please enter a region name.');
      return;
    }
    if (selectedKeywords.length===0) {
      setErrorMsg('Select or add at least one keyword.');
      return;
    }
    setLoading(true);
    try {
      await api.projects.upsertRegionAndKeywords(currentProject.id, {
        keyword_region: regionName.trim(),
        keywords: selectedKeywords.map(k=>k.text)
      });
      setSuccessMsg('Keywords saved!');
      setTimeout(()=>setSuccessMsg(''),3000);
      
      // Reload the current project to update data
      await loadCurrentProject(currentProject.id, { force: true });
      
      // Only navigate if not in settings mode
      if (!isSettings) {
        navigate('/onboarding/integrations');
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.error||err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      {!isSettings && (
        <>
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Keywords & Regions
          </h1>
          <p className="text-center text-gray-600 mb-6">
            Group your target keywords by region
          </p>
        </>
      )}

      {errorMsg && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div className="bg-green-100 text-green-800 p-3 rounded mb-4">
          {successMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Region */}
        <div>
          <label
            htmlFor="regionName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Keyword Region
          </label>
          <input
            id="regionName"
            type="text"
            value={regionName}
            onChange={e=>setRegionName(e.target.value)}
            placeholder="e.g. India"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Generate Keywords Button */}
        <div>
          <button
            type="button"
            onClick={generateSuggestions}
            disabled={isGenerating || !currentProject?.website || !currentProject?.description}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1ba6ff] hover:bg-[#1697eb] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1ba6ff] disabled:opacity-50"
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating AI Suggestions...
              </>
            ) : (
              'Generate AI Keyword Suggestions'
            )}
          </button>
          {!currentProject?.website || !currentProject?.description ? (
            <p className="mt-2 text-sm text-gray-500 text-center">
              Complete business details first to generate suggestions
            </p>
          ) : null}
        </div>

        {/* Custom Keyword */}
        <div>
          <label
            htmlFor="customKeyword"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Add Your Own Keyword
          </label>
          <div className="flex gap-2">
            <input
              id="customKeyword"
              type="text"
              value={customInput}
              onChange={e=>setCustomInput(e.target.value)}
              onKeyDown={handleCustomKey}
              placeholder="Type keyword and press Enter"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="button"
              onClick={addCustom}
              className="px-4 py-2 bg-[#1ba6ff] text-white rounded-r-md hover:bg-[#1697eb]"
            >
              Add
            </button>
          </div>
        </div>

        {/* Selected */}
        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-2">
            Selected Keywords ({selectedKeywords.length})
          </h2>
          <div className="flex flex-wrap gap-2 bg-white p-3 border border-gray-200 rounded min-h-[3rem]">
            {selectedKeywords.length>0 
              ? selectedKeywords.map(k=>(
                <span
                  key={k.text}
                  className="bg-[#1ba6ff]/10 text-[#1ba6ff] px-3 py-1 rounded-full text-sm flex items-center"
                >
                  {k.text}
                  <button
                    type="button"
                    onClick={()=>handleSelect(k)}
                    className="ml-1 text-[#1ba6ff] hover:text-[#1697eb]"
                  >
                    &times;
                  </button>
                </span>
              ))
              : <p className="text-gray-500 text-sm">No keywords yet</p>
            }
          </div>
        </div>

        {/* Suggestions */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Suggested Keywords
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {keywords.map(k=>(
              <KeywordItem
                key={k.id}
                keyword={k}
                selected={!!selectedKeywords.find(s=>s.text===k.text)}
                onSelect={handleSelect}
              />
            ))}
          </div>
          {isGenerating ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center px-4 py-2 bg-[#1ba6ff]/10 rounded-lg">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#1ba6ff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-[#1ba6ff] font-medium">Generating AI Suggestions...</span>
              </div>
            </div>
          ) : keywords.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Click "Generate AI Keyword Suggestions" to get started
            </p>
          ) : null}
        </div>

        {/* Continue */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={loading || isGenerating}
            className="px-6 py-3 bg-[#1ba6ff] text-white rounded-md hover:bg-[#1697eb] disabled:opacity-50"
          >
            {loading ? 'Saving…' : isSettings ? 'Save' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default KeywordsPage;
